<script setup>
import { Button } from '@dashhudson/dashing-ui/button';
import { useDrawer } from '@dashhudson/dashing-ui/drawer';
import { Section } from '@dashhudson/dashing-ui/section';
import { SectionHeader } from '@dashhudson/dashing-ui/section-header';
import { computed, onMounted, ref, watch } from 'vue';

import { useListeningPermissions } from '@/app/socialListening/composables/useListeningPermissions';
import { usePostDisplayFormatToggle } from '@/app/socialListening/composables/usePostDisplayFormatToggle.js';
import { useSocialListening } from '@/app/socialListening/composables/useSocialListening';
import {
  NO_COMMENTS_FOUND,
  POSTS_VIEW,
  SOCIAL_LISTENING_DRAWERS,
  SOCIAL_LISTENING_REPORT_SECTIONS,
  SOCIAL_LISTENING_TOPIC_TOOLTIPS,
  TOPIC_COMPARISONS_TOOLTIPS,
} from '@/app/socialListening/constants';
import { MENTION_TYPE_FILTER_OPTIONS } from '@/app/socialListening/constants/listeningFilters';
import PostsDataTable from '@/app/socialListening/drawers/components/PostsDataTable.vue';
import { formatPostDataToDataTableProps } from '@/app/socialListening/utils/formatters';
import { useSocialListeningComparisonsStore } from '@/stores/social-listening-comparisons';
import { useSocialListeningTopPostsStore } from '@/stores/social-listening-top-posts';
import { useSocialListeningTopicsStore } from '@/stores/social-listening-topics';
import { useTrackingStore } from '@/stores/tracking';

// Composables
const { launchDrawer, currentDrawer } = useDrawer();
const { isTopicsPage, isComparisonPage } = useSocialListening();
const { setView } = usePostDisplayFormatToggle();
const { commentsEnabled } = useListeningPermissions();
// Stores
const topicsStore = useSocialListeningTopicsStore();
const comparisonStore = useSocialListeningComparisonsStore();
const topPostsStore = useSocialListeningTopPostsStore();
const trackingStore = useTrackingStore();

// Props
const props = defineProps({
  loading: {
    type: Boolean,
    default: false,
  },
  drawerUsage: {
    type: Boolean,
    default: false,
  },
});

// Refs
const commentsPreviewList = ref([]);

// Computed
const viewCommentsDrawer = computed(
  () => currentDrawer?.value?.name === SOCIAL_LISTENING_DRAWERS.COMMENTS,
);

const isReport = computed(() => trackingStore.currentRoute?.meta?.report);

const commentsLists = computed(() => topPostsStore.topicsCommentsPreview);

const commentsLoading = computed(() => topPostsStore.pending.topicsCommentsPreview);

const formattedCommentsPreview = computed(() =>
  (commentsLists.value ?? []).map((comment) => formatPostDataToDataTableProps(comment)),
);

const isEmpty = computed(
  () => formattedCommentsPreview.value?.length === 0 && !commentsLoading.value,
);

const displayedComments = computed(() => {
  if (props.drawerUsage) {
    return formattedCommentsPreview.value;
  }
  return commentsPreviewList.value;
});

const tooltip = computed(() => {
  if (isReport.value) {
    return null;
  }
  if (isTopicsPage.value) {
    return SOCIAL_LISTENING_TOPIC_TOOLTIPS.COMMENTS;
  }
  if (isComparisonPage.value) {
    return TOPIC_COMPARISONS_TOOLTIPS.COMMENTS;
  }
  return null;
});

// Functions
function openDrawer(source, sourceId) {
  let clickedCommentProps;
  // The drawer will open with comments only (no other types are expected in the list)
  const allCommentsProps = (commentsLists.value ?? []).map((comment) => {
    const commentProps = {
      source: comment.source,
      commentSourceId: comment.sourceId,
      postSourceId: comment.commentPostSourceId,
    };
    if (comment.source === source && comment.sourceId === sourceId) {
      clickedCommentProps = commentProps;
    }
    return commentProps;
  });

  launchDrawer({
    name: SOCIAL_LISTENING_DRAWERS.MEDIA_POST,
    propsList: allCommentsProps,
    props: clickedCommentProps,
  });
}

function handleRowClick(row) {
  openDrawer(row.source, row.sourceId);
}

function onSeeMore() {
  setView(POSTS_VIEW.LIST);
  const drawerProps = { name: SOCIAL_LISTENING_DRAWERS.COMMENTS };

  if (isTopicsPage.value) {
    drawerProps.props = {
      ...topicsStore.drawerSerializedFilters,
      mentionTypes: [MENTION_TYPE_FILTER_OPTIONS.COMMENT.value],
      sortBy: 'date',
      sortOrder: 'DESC',
      initialView: POSTS_VIEW.LIST,
      tooltip: SOCIAL_LISTENING_TOPIC_TOOLTIPS.PREVIEW_DRAWER,
      section: SOCIAL_LISTENING_REPORT_SECTIONS.COMMENTS,
    };
  }
  if (isComparisonPage.value) {
    const { keywordsAndHashtags, ...filterData } = comparisonStore.temporaryFilters;

    const selectedTopic = topicsStore.selectedTopic;
    comparisonStore.setDrawerSelectedTopic(selectedTopic);
    comparisonStore.setTempFilters({
      keywordsAndHashtags: selectedTopic.searchBody.filters.keywordsAndHashtags,
      excludedHandles: selectedTopic.searchBody.filters.excludedHandles ?? [],
      ...filterData,
    });
    drawerProps.props = {
      ...comparisonStore.drawerSerializedFilters,
      keywordsAndHashtags: selectedTopic.searchBody.filters.keywordsAndHashtags,
      excludedHandles: selectedTopic.searchBody.filters.excludedHandles ?? [],
      topicId: selectedTopic.id,
      mentionTypes: [MENTION_TYPE_FILTER_OPTIONS.COMMENT.value],
      sortBy: 'date',
      sortOrder: 'DESC',
      initialView: POSTS_VIEW.LIST,
      tooltip: TOPIC_COMPARISONS_TOOLTIPS.PREVIEW_DRAWER,
      section: SOCIAL_LISTENING_REPORT_SECTIONS.COMMENTS,
    };
  }

  launchDrawer(drawerProps);
}

// Lifecycle
onMounted(() => {
  if (!props.drawerUsage) {
    topPostsStore.clearCommentsPreview();
  }
});

// Watchers
watch(
  () => formattedCommentsPreview.value,
  (to) => {
    if (to && !viewCommentsDrawer.value) {
      commentsPreviewList.value = formattedCommentsPreview.value;
    }
  },
);
</script>

<template>
  <Section v-if="commentsEnabled">
    <slot name="header">
      <SectionHeader title="Comments" :tooltip="tooltip" />
    </slot>
    <PostsDataTable
      :posts="displayedComments"
      :loading="commentsLoading"
      drawer-id="comments-preview-table"
      :on-row-click="handleRowClick"
      :can-load-more="() => false"
      :on-load-more="() => {}"
      :empty-state="{
        message: NO_COMMENTS_FOUND,
      }"
      caption-header="Comment"
    />
    <div class="flex flex-row !items-center !justify-center">
      <Button
        v-if="!isEmpty && !isReport"
        data-test="see-more-button"
        label="See More"
        severity="tertiary"
        :disabled="commentsLoading || viewCommentsDrawer"
        @click="onSeeMore"
      />
    </div>
  </Section>
</template>
